import { optionalOverrideJustificationSchema } from 'features/libra/overrides/overrideJustification'
import { z } from 'zod'

/**
 * NII strategy, justification, and HOIS-RP-103 coverage logic — the decision
 * tree, its schemas, the modifier lookup tables, and the coverage math. Split
 * out of `zoneAnalysisApi.ts` to keep that file focused on the zone shape +
 * ticket parsers/hooks.
 *
 * This module never depends on `zoneAnalysisApi`, so the dependency stays
 * one-directional (api → niiStrategy). Functions that need
 * zone data take the structural `CoverageZone` subset below rather than the full
 * `ZoneParams` (which `zoneAnalysisApi` composes from these schemas), so there
 * is no import cycle.
 */

// ============================================================================
// Shared Types
// ============================================================================

export type TriState = 'low' | 'medium' | 'high'

export enum InspectionStrategy {
  A = 'A',
  B = 'B',
  C = 'C',
}

// Consequence of Failure Schema (Global - applies to all zones)
export const consequenceOfFailureSchema = z.object({
  level: z.enum(['low', 'medium', 'high']).default('medium'),
  justification: z.string().optional(),
})

// NII Strategy Schema
export const niiStrategySchema = z.object({
  strategy_type_calculated: z.nativeEnum(InspectionStrategy).optional(),
  strategy_type_override: z.nativeEnum(InspectionStrategy).optional(),
  strategy_type_override_justification: optionalOverrideJustificationSchema,
  degradation_likely: z.boolean().optional(),
  degradation_likely_justification: z.string().optional(),
  degradation_predictable: z.boolean().optional(),
  degradation_predictable_justification: z.string().optional(),
  degradation_severity: z.enum(['low', 'medium', 'high']).optional(),
  degradation_severity_justification: z.string().optional(),
  // Top-level justification: auto-set to the last answered step's justification
  justification: z.string().optional(),
  // Confidence from NII Screening (read-only, used for coverage calculations)
  confidence: z.enum(['low', 'med', 'high']).optional(),
})

// RBA Factor Schema
export const rbaFactorSchema = z.object({
  value: z.enum(['low', 'medium', 'high']).default('medium'),
  modifier: z.number().default(1.0),
  reason: z.string().optional(),
  informs: z.array(z.string()).optional(),
})

// RBA Information Schema
export const rbaInformationSchema = z.object({
  // Type A & B: Density is used for F_COV lookup (not a standalone modifier)
  density_of_degradation: z.enum(['low', 'medium', 'high']).optional(),
  density_justification: z.string().optional(),

  // Type B only: Additional modifiers
  measurement_accuracy: z.enum(['low', 'medium', 'high']).optional(), // F_ACC
  measurement_accuracy_justification: z.string().optional(),
  tolerance_of_degradation: z.enum(['low', 'medium', 'high']).optional(), // F_LC
  tolerance_justification: z.string().optional(),
  spatial_homogeneity: z.enum(['low', 'medium', 'high']).optional(), // F_SH
  spatial_homogeneity_justification: z.string().optional(),
})

// Coverage Calculation Schema
export const coverageCalculationSchema = z.object({
  // Type A & B
  base_coverage_percent: z.number().optional(), // C_1 (7.5% or 25%)

  // Individual modifiers (for display/debugging)
  f_cov: z.number().optional(), // From Confidence × Density lookup
  f_acc: z.number().optional(), // Type B only: Measurement accuracy
  f_lc: z.number().optional(), // Type B only: Tolerance to degradation
  f_sh: z.number().optional(), // Type B only: Spatial homogeneity
  f_cons: z.number().optional(), // Consequence of failure
  f_zone: z.number().optional(), // Zone area modifier

  // Pre-clamp result (product of modifiers × base coverage, before min/max)
  raw_calculated_percent: z.number().optional(),

  // Final results (after clamping)
  required_coverage_percent: z.number().optional(),
  required_coverage_area_sq_m: z.number().optional(),
})

export type ConsequenceOfFailure = z.infer<typeof consequenceOfFailureSchema>
export type NiiStrategy = z.infer<typeof niiStrategySchema>
export type RbaFactor = z.infer<typeof rbaFactorSchema>
export type RbaInformation = z.infer<typeof rbaInformationSchema>
export type CoverageCalculation = z.infer<typeof coverageCalculationSchema>

/**
 * Structural subset of `ZoneParams` that the coverage math and area accessor
 * read. Declared here (instead of importing `ZoneParams`) so this module never
 * depends on `zoneAnalysisApi`; the full `ZoneParams` is structurally
 * assignable, so callers pass a whole zone unchanged.
 */
export type CoverageZone = {
  nii_strategy?: NiiStrategy
  rba_info?: RbaInformation
  total_surface_area_sq_m_override?: number
  surface_area_calculated_sq_m?: number
  total_surface_area_sq_m?: number
  calculated_area_sq_m?: number
}

// ============================================================================
// HOIS-RP-103 Lookup Tables and Modifiers
// ============================================================================

/**
 * F_CONS - Consequence of Failure Modifier (same for Type A and B)
 */
export function getCofModifier(level: TriState): number {
  const modifiers: Record<TriState, number> = {
    low: 0.6,
    medium: 0.8,
    high: 1.0,
  }
  return modifiers[level]
}

/**
 * F_COV - Coverage Modifier (Confidence × Density lookup)
 * Source: Table 4-9 (Type A), Table 4-11 (Type B) - same values for both
 */
export function getFCov(
  confidence: 'low' | 'med' | 'high',
  density: TriState
): number {
  const table: Record<'low' | 'med' | 'high', Record<TriState, number>> = {
    low: { high: 1.25, medium: 2.0, low: 4.0 },
    med: { high: 0.75, medium: 1.0, low: 2.0 },
    high: { high: 0.5, medium: 0.75, low: 1.25 },
  }
  return table[confidence][density]
}

/**
 * F_ZONE - Zone Area Modifier
 * Source: Table 4-10 (Type A), Table 4-16 (Type B) - same values for both
 */
export function getFZone(areaM2: number): number {
  if (areaM2 < 5) return 2.0
  if (areaM2 < 10) return 1.5
  if (areaM2 < 50) return 1.0
  if (areaM2 < 100) return 0.75
  return 0.5
}

/**
 * F_ACC - Measurement Accuracy Modifier (Type B only)
 * Source: Table 4-12
 */
export function getFAcc(accuracy: TriState): number {
  const modifiers: Record<TriState, number> = {
    high: 1.0,
    medium: 1.15,
    low: 1.25,
  }
  return modifiers[accuracy]
}

/**
 * F_LC - Tolerance to Degradation Modifier (Type B only)
 * Source: Table 4-13
 */
export function getFLc(tolerance: TriState): number {
  const modifiers: Record<TriState, number> = {
    high: 0.8,
    medium: 1.0,
    low: 1.2,
  }
  return modifiers[tolerance]
}

/**
 * F_SH - Spatial Homogeneity Modifier (Type B only)
 * Source: Table 4-15
 */
export function getFSh(homogeneity: TriState): number {
  const modifiers: Record<TriState, number> = {
    high: 1.0,
    medium: 1.15,
    low: 1.25,
  }
  return modifiers[homogeneity]
}

/**
 * Type B Floor and Ceiling based on Confidence
 * Source: Table 4-17
 */
export function getTypeBLimits(confidence: 'low' | 'med' | 'high'): {
  min: number
  max: number
} {
  const limits: Record<'low' | 'med' | 'high', { min: number; max: number }> = {
    high: { min: 7.5, max: 40 },
    med: { min: 12.5, max: 50 },
    low: { min: 20, max: 80 },
  }
  return limits[confidence]
}

export function getBaseCoverage(strategyType: InspectionStrategy): number {
  const baseCoverages: Record<InspectionStrategy, number> = {
    A: 7.5,
    B: 25,
    C: 100,
  }
  return baseCoverages[strategyType]
}

// ============================================================================
// Decision tree + justification
// ============================================================================

/**
 * Resolve the decision tree only when every question required by the active
 * path has an answer. Later answers may exist (for example from AI) while an
 * earlier branch is missing; those answers remain reviewable but must not
 * manufacture a default strategy.
 */
export function calculateNiiStrategyType(
  strategy?: NiiStrategy
): InspectionStrategy | undefined {
  if (!strategy || strategy.degradation_likely === undefined) return undefined
  if (strategy.degradation_likely === false) return InspectionStrategy.A
  if (strategy.degradation_predictable === undefined) return undefined
  if (strategy.degradation_predictable === false) return InspectionStrategy.C
  if (strategy.degradation_severity === undefined) return undefined
  return strategy.degradation_severity === 'high'
    ? InspectionStrategy.C
    : InspectionStrategy.B
}

/**
 * Replace backend null-sentinel prose ("RL = None") with SME-facing wording.
 *
 * Belt-and-braces for the NII justification strings: the backend no longer
 * emits the sentinels (gong-agent #54), but manual-edit derivations and any
 * older persisted values still flow through here. Lives beside
 * `deriveNiiJustification` because both the AI path and `NiiStrategyPanel`'s
 * manual-edit path use it.
 */
export function sanitizeNiiJustification(
  value: string | undefined
): string | undefined {
  if (value === undefined) return undefined
  return value.replace(
    /\bRL\s*=\s*(?:none|null|undefined|n\/?a)\s*(?:years?|yrs?|yr)?\b/gi,
    'RL unavailable'
  )
}

/**
 * The top-level justification is the justification of the last question the
 * active decision path actually reached: Q1 when degradation is unlikely (or
 * unanswered), Q2 when degradation is not predictable, else Q3. Single-sourced
 * here so the panel's live edits and the AI-fill finalize step stay in lockstep.
 */
export function deriveNiiJustification(
  strategy: NiiStrategy
): string | undefined {
  if (strategy.degradation_likely === true) {
    if (strategy.degradation_predictable === false) {
      return strategy.degradation_predictable_justification
    }
    if (
      strategy.degradation_predictable === true &&
      strategy.degradation_severity !== undefined
    ) {
      return strategy.degradation_severity_justification
    }
    return strategy.degradation_predictable_justification
  }
  return strategy.degradation_likely_justification
}

/**
 * Which decision-tree questions the active NII path actually reaches. When an
 * earlier answer flips (e.g. Q1 → No) the later answers are intentionally NOT
 * cleared — the AI's Q2/Q3 stay reviewable in case the branch becomes
 * applicable again — so any consumer that renders the raw fields must gate on
 * this, or it will present off-path (stale) answers as authoritative. Q1 is
 * always reached; Q2 only when Q1 = Yes; Q3 only when Q1 = Yes and Q2 = Yes.
 */
export function niiPathReaches(strategy?: NiiStrategy): {
  predictable: boolean
  severity: boolean
} {
  const predictable = strategy?.degradation_likely === true
  const severity = predictable && strategy?.degradation_predictable === true
  return { predictable, severity }
}

export function getEffectiveStrategyType(
  strategy?: NiiStrategy
): InspectionStrategy | undefined {
  return strategy?.strategy_type_override ?? getCalculatedStrategyType(strategy)
}

/** The strategy before any manual override. */
export function getCalculatedStrategyType(
  strategy?: NiiStrategy
): InspectionStrategy | undefined {
  if (!strategy) return undefined
  const calculated = calculateNiiStrategyType(strategy)
  if (calculated) return calculated

  // Backward compatibility for legacy zones that stored only a calculated
  // type. Once any decision answer exists, an incomplete path intentionally
  // returns undefined rather than trusting a stale saved default.
  const hasDecisionAnswer =
    strategy.degradation_likely !== undefined ||
    strategy.degradation_predictable !== undefined ||
    strategy.degradation_severity !== undefined
  return hasDecisionAnswer ? undefined : strategy.strategy_type_calculated
}

// ============================================================================
// Coverage
// ============================================================================

/**
 * The zone's area, in precedence order — THE single definition, because this
 * number selects F_ZONE and multiplies through to the required coverage area
 * that reaches the deliverable. Every consumer must read it through here: the
 * zone card used to resolve `calculated_area_sq_m` ahead of
 * `total_surface_area_sq_m` (the reverse of this order), which was harmless only
 * while the two producers could never both be set. The surface-area calculator
 * populates drawing-based zones, so they can be, and a card displaying one area
 * while coverage was computed from another is exactly the bug that ordering
 * would now cause.
 *
 * 1. `total_surface_area_sq_m_override` — the SME typed it. Always wins.
 * 2. `surface_area_calculated_sq_m` — the surface-area calculator.
 * 3. `total_surface_area_sq_m` — legacy/AI-supplied value on older tickets.
 * 4. `calculated_area_sq_m` — asset-model rectangles (the legacy ~1% path).
 */
export function getEffectiveZoneArea(zone: CoverageZone): number {
  return (
    zone.total_surface_area_sq_m_override ?? getCalculatedZoneArea(zone) ?? 0
  )
}

/** The zone's area before any override: precedence steps 2–4 above. */
export function getCalculatedZoneArea(zone: CoverageZone): number | undefined {
  return (
    zone.surface_area_calculated_sq_m ??
    zone.total_surface_area_sq_m ??
    zone.calculated_area_sq_m
  )
}

/**
 * Calculate required coverage per HOIS-RP-103
 * @param zone - The zone being analyzed
 * @param globalCofModifier - Global CoF modifier (from ConsequenceOfFailure section)
 * @param confidence - Confidence from NII Screening ('low' | 'med' | 'high')
 */
export function calculateRequiredCoverage(
  zone: CoverageZone,
  globalCofModifier: number,
  confidence: 'low' | 'med' | 'high'
): CoverageCalculation | undefined {
  const strategy = zone.nii_strategy
  const rba = zone.rba_info

  if (!strategy) return undefined

  const effectiveStrategyType = getEffectiveStrategyType(strategy)
  if (!effectiveStrategyType) return undefined

  const baseCoverage = getBaseCoverage(effectiveStrategyType)
  const totalArea = getEffectiveZoneArea(zone)

  // Type C: Always 100%
  if (effectiveStrategyType === InspectionStrategy.C) {
    return {
      base_coverage_percent: 100,
      required_coverage_percent: 100,
      required_coverage_area_sq_m: totalArea,
      f_cons: globalCofModifier,
    }
  }

  // Get shared modifiers for Type A and B
  const density = rba?.density_of_degradation ?? 'medium'
  const fCov = getFCov(confidence, density)
  const fCons = globalCofModifier
  const fZone = getFZone(totalArea)

  // Type A: C_R = max(2.5%, F_COV × F_CONS × F_ZONE × 7.5%)
  if (effectiveStrategyType === InspectionStrategy.A) {
    const calculated = fCov * fCons * fZone * baseCoverage
    const requiredCoveragePercent = Math.max(2.5, calculated)
    const requiredCoverageArea = totalArea * (requiredCoveragePercent / 100)

    return {
      base_coverage_percent: baseCoverage,
      f_cov: fCov,
      f_cons: fCons,
      f_zone: fZone,
      raw_calculated_percent: calculated,
      required_coverage_percent: requiredCoveragePercent,
      required_coverage_area_sq_m: requiredCoverageArea,
    }
  }

  // Type B: C_R = clamp(C_min, C_max, F_COV × F_ACC × F_LC × F_CONS × F_SH × F_ZONE × 25%)
  if (effectiveStrategyType === InspectionStrategy.B) {
    const accuracy = rba?.measurement_accuracy ?? 'medium'
    const tolerance = rba?.tolerance_of_degradation ?? 'medium'
    const homogeneity = rba?.spatial_homogeneity ?? 'medium'

    const fAcc = getFAcc(accuracy)
    const fLc = getFLc(tolerance)
    const fSh = getFSh(homogeneity)

    const calculated = fCov * fAcc * fLc * fCons * fSh * fZone * baseCoverage
    const { min, max } = getTypeBLimits(confidence)
    const requiredCoveragePercent = Math.min(max, Math.max(min, calculated))
    const requiredCoverageArea = totalArea * (requiredCoveragePercent / 100)

    return {
      base_coverage_percent: baseCoverage,
      f_cov: fCov,
      f_acc: fAcc,
      f_lc: fLc,
      f_cons: fCons,
      f_sh: fSh,
      f_zone: fZone,
      raw_calculated_percent: calculated,
      required_coverage_percent: requiredCoveragePercent,
      required_coverage_area_sq_m: requiredCoverageArea,
    }
  }

  return undefined
}
